import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Heart, ShoppingBag, BadgeCheck, ChevronRight, Truck, ShieldCheck, FileText,
  Minus, Plus, MapPin, Store,
} from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatINR } from "@/lib/format";
import { Rating } from "@/components/marketplace/Rating";
import { ProductCard } from "@/components/marketplace/ProductCard";
import { LoadingState } from "@/components/common/States";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, inWishlist, b2bMode, user } = useStore();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [pincode, setPincode] = useState("");
  const [deliveryMsg, setDeliveryMsg] = useState("");
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    setData(null);
    setActive(0);
    setQty(1);
    setVariant(null);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.product.seo_title || r.data.product.name} | Shri Vrindavan Shop`;
      let m = document.querySelector('meta[name="description"]');
      if (m && (r.data.product.seo_description || r.data.product.short_description)) m.content = r.data.product.seo_description || r.data.product.short_description;
      setQty(1);
    });
  }, [slug]);

  if (!data) return <LoadingState label="Loading product..." />;
  const p = data.product;
  const isBusiness = user && user.role === "business_buyer";
  const showB2b = b2bMode && isBusiness && p.b2b_enabled && p.wholesale_unit_price != null;
  const price = showB2b ? p.wholesale_unit_price : (variant ? variant.price : p.price);
  const mrp = variant ? variant.mrp : p.mrp;
  const wished = inWishlist(p.slug);

  const checkPincode = (e) => {
    e.preventDefault();
    if (/^\d{6}$/.test(pincode)) setDeliveryMsg(`Deliverable to ${pincode} • Estimated 3–7 days (Pan India)`);
    else setDeliveryMsg("Please enter a valid 6-digit pincode.");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <nav className="flex items-center gap-1 text-xs text-stone-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-secondary">Home</Link>
        <ChevronRight size={12} />
        <Link to={`/category/${p.category}`} className="hover:text-secondary">{p.category_name}</Link>
        <ChevronRight size={12} />
        <span className="text-stone-800 line-clamp-1">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Gallery */}
        <div>
          <div className="rounded-2xl overflow-hidden border border-border bg-muted aspect-square" data-testid="product-gallery-main">
            <img src={p.images[active]} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {p.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`h-20 w-20 rounded-lg overflow-hidden border-2 ${active === i ? "border-primary" : "border-border"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <Link to={`/vendor/${p.vendor_slug}`} className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-secondary">
            <Store size={14} /> {p.vendor_name}
            {p.vendor_verified && <BadgeCheck size={14} className="text-emerald-600" />}
          </Link>
          <h1 data-testid="product-name" className="mt-2 text-2xl sm:text-3xl font-serif font-bold text-stone-900">{p.name}</h1>
          <div className="mt-3 flex items-center gap-4">
            <Rating value={p.rating} count={p.reviews_count} size={16} />
            {p.b2b_available && (
              <span className="text-[11px] font-semibold text-blue-800 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">B2B Available</span>
            )}
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-secondary" data-testid="product-price">{formatINR(price)}</span>
            {!showB2b && mrp > price && (
              <>
                <span className="text-lg text-stone-400 line-through">{formatINR(mrp)}</span>
                <span className="text-sm font-bold text-red-600">{Math.round((mrp - price) / mrp * 100)}% OFF</span>
              </>
            )}
            {showB2b && <span className="text-sm text-stone-500">/ pc wholesale • Min order {p.minimum_order_quantity} units</span>}
          </div>
          <p data-testid="product-stock-status" className={`mt-1 text-sm font-medium ${p.stock_status_code === "LOW_STOCK" ? "text-amber-600" : p.in_stock ? "text-emerald-600" : "text-red-500"}`}>{p.stock_status}{p.stock_status_code === "LOW_STOCK" && p.available_quantity ? ` · only ${p.available_quantity} left` : ""}</p>
          {(p.badges || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-testid="product-badges">{p.badges.map((b) => <span key={b} data-testid={`product-badge-${b.toLowerCase()}`} className="rounded-full bg-accent text-secondary text-[11px] font-bold tracking-wide px-2 py-0.5">{b.replace("_", " ")}</span>)}</div>}
          {p.variants?.length > 0 && (
            <div className="mt-4" data-testid="variant-selector">
              <div className="text-sm font-semibold text-stone-900 mb-2">Choose option</div>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => <button key={v.id} onClick={() => setVariant(variant?.id === v.id ? null : v)} data-testid={`variant-${v.sku}`} className={`rounded-lg border px-3 py-1.5 text-sm ${variant?.id === v.id ? "border-secondary bg-secondary text-white" : "border-border text-stone-700 hover:border-secondary"}`}>{v.variant_name} · {formatINR(v.price)}</button>)}
              </div>
            </div>
          )}

          <p className="mt-4 text-stone-600 leading-relaxed">{p.description}</p>

          {/* Wholesale info */}
          {p.b2b_enabled && (
            <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50/50 p-4" data-testid="wholesale-info">
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Wholesale Available</h4>
              <div className="text-sm text-stone-700">Minimum order quantity: <b data-testid="wholesale-moq">{p.minimum_order_quantity} units</b></div>
              {p.wholesale_unit_price != null ? (
                <div className="text-sm text-stone-700">Wholesale unit price: <b className="text-blue-900" data-testid="wholesale-price">{formatINR(p.wholesale_unit_price)}</b> / unit</div>
              ) : (
                <div className="text-xs text-stone-500 mt-1" data-testid="wholesale-price-hidden">{user ? "Wholesale pricing is available to verified business buyer accounts." : <>Sign in as a business buyer to view wholesale pricing. <Link to="/login" className="font-semibold text-secondary">Sign in</Link></>}</div>
              )}
              {isBusiness && (
                <Link to={`/b2b/request-quote?product=${p.slug}${variant ? `&variant=${variant.id}` : ""}&qty=${Math.max(qty, p.minimum_order_quantity)}`} data-testid="detail-request-quote" className="mt-3 inline-flex items-center gap-2 rounded-full border border-secondary text-secondary text-sm font-semibold px-4 py-2 hover:bg-secondary hover:text-white transition-colors">
                  <FileText size={15} /> Request Wholesale Quote
                </Link>
              )}
            </div>
          )}

          {/* Qty + actions */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center rounded-lg border border-border">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="h-11 w-11 grid place-items-center hover:bg-muted"><Minus size={16} /></button>
              <span className="w-12 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" className="h-11 w-11 grid place-items-center hover:bg-muted"><Plus size={16} /></button>
            </div>

            {showB2b ? (
              <button onClick={() => addToCart(p, Math.max(qty, p.minimum_order_quantity), variant, { b2b: true })} disabled={!p.in_stock || (p.variants?.length > 0 && !variant)} data-testid="detail-add-to-cart-wholesale" className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 rounded-lg bg-secondary text-white font-semibold py-3 hover:bg-[#5C061C] transition-colors disabled:opacity-50">
                <ShoppingBag size={18} /> Add {Math.max(qty, p.minimum_order_quantity)} at wholesale
              </button>
            ) : (
              <button onClick={() => addToCart(p, qty, variant)} disabled={!p.in_stock || (p.variants?.length > 0 && !variant)} data-testid="detail-add-to-cart" className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground font-semibold py-3 hover:bg-[hsl(28_90%_38%)] transition-colors disabled:opacity-50">
                <ShoppingBag size={18} /> {p.variants?.length > 0 && !variant ? "Select an option" : "Add to Cart"}
              </button>
            )}
            <button onClick={() => toggleWishlist(p)} data-testid="detail-wishlist" className="h-11 w-11 grid place-items-center rounded-lg border border-border hover:border-secondary">
              <Heart size={18} className={wished ? "fill-secondary text-secondary" : "text-stone-500"} />
            </button>
          </div>

          {/* Pincode */}
          <form onSubmit={checkPincode} className="mt-6 flex items-center gap-2 max-w-sm">
            <div className="flex-1 flex items-center rounded-lg border border-border overflow-hidden">
              <MapPin size={16} className="ml-3 text-primary" />
              <input value={pincode} onChange={(e) => setPincode(e.target.value)} data-testid="pincode-input" placeholder="Enter delivery pincode" maxLength={6} className="flex-1 px-3 py-2.5 text-sm outline-none" />
            </div>
            <button data-testid="pincode-check" className="rounded-lg border border-secondary text-secondary px-4 py-2.5 text-sm font-semibold">Check</button>
          </form>
          {deliveryMsg && <p className="mt-2 text-sm text-stone-600 flex items-center gap-1.5"><Truck size={14} className="text-primary" /> {deliveryMsg}</p>}

          <div className="mt-6 flex flex-wrap gap-4 text-xs text-stone-500">
            <span className="flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-600" /> Secure Checkout</span>
            <span className="flex items-center gap-1"><FileText size={14} className="text-primary" /> GST Invoice</span>
            <span className="flex items-center gap-1"><Truck size={14} className="text-primary" /> Pan India Delivery</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-12">
        <Tabs defaultValue="desc">
          <TabsList data-testid="product-tabs">
            <TabsTrigger value="desc" data-testid="tab-desc">Description</TabsTrigger>
            <TabsTrigger value="specs" data-testid="tab-specs">Specifications</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
          </TabsList>
          <TabsContent value="desc" className="pt-4 text-stone-600 leading-relaxed max-w-3xl">
            {p.description} Handcrafted with devotion and quality by {p.vendor_name}, {p.vendor_location}.
          </TabsContent>
          <TabsContent value="specs" className="pt-4">
            <table className="w-full max-w-lg text-sm">
              <tbody>
                {Object.entries(p.specs).map(([k, v]) => (
                  <tr key={k} className="border-b border-border">
                    <td className="py-2.5 font-medium text-stone-500 w-1/3">{k}</td>
                    <td className="py-2.5 text-stone-800">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TabsContent>
          <TabsContent value="reviews" className="pt-4">
            <div className="flex items-center gap-3">
              <Rating value={p.rating} count={p.reviews_count} size={18} />
              <span className="text-sm text-stone-500">Based on {p.reviews_count} verified reviews</span>
            </div>
            <p className="mt-3 text-sm text-stone-500">Detailed customer reviews will be available in the next development phase.</p>
          </TabsContent>
        </Tabs>
      </div>

      {/* Related */}
      {data.related?.length > 0 && (
        <div className="mt-14">
          <h2 className="text-2xl font-serif font-bold text-stone-900 mb-6">You May Also Like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {data.related.map((rp) => <ProductCard key={rp.slug} product={rp} />)}
          </div>
        </div>
      )}
    </div>
  );
}
